/**
 * Permission Manager Explorer
 *
 * Admin-only explorer for managing permission -> operation mappings.
 * Filters are managed by CBE's built-in filter system (quickFilters).
 * Custom toolbar provides grant form, cache controls, and import/export.
 * Delete buttons per row for revoking mappings.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useFusionAuth } from '@fusionauth/react-sdk';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import permissionManagerConfig from './PermissionManagerConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload } from '@fortawesome/free-solid-svg-icons';
import {
  fetchOperations,
  fetchMappings,
  createMapping,
  deleteMapping,
  fetchCacheInfo,
  rebuildCache,
  exportMappings,
} from '../../../services/permissionAdminService';
import type { FeatureOperation, CacheInfo } from '../../../services/permissionAdminService';
import ImportPermissionsModal from './ImportPermissionsModal';
import './PermissionManagerExplorer.scss';

interface PermissionManagerExplorerProps {
  className?: string;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
  onClose?: () => void;
}

const PermissionManagerExplorer: React.FC<PermissionManagerExplorerProps> = ({
  className,
  initialPosition = { x: 400, y: 150 },
  initialSize = { width: 1200, height: 700 },
  onClose,
}) => {
  const { isLoggedIn } = useFusionAuth();

  // refreshTrigger for grant/delete/import operations (not filters — CBE handles those)
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ─── Operations data (for grant form dropdowns) ────────────────────────
  const [operations, setOperations] = useState<FeatureOperation[]>([]);
  const [distinctPermissions, setDistinctPermissions] = useState<string[]>([]);

  // ─── Grant form state ──────────────────────────────────────────────────
  const [grantPermission, setGrantPermission] = useState('');
  const [grantFeature, setGrantFeature] = useState('');
  const [grantOperation, setGrantOperation] = useState('');
  const [grantError, setGrantError] = useState('');
  const [grantSuccess, setGrantSuccess] = useState('');
  const [granting, setGranting] = useState(false);

  // ─── Cache info ────────────────────────────────────────────────────────
  const [cacheInfo, setCacheInfo] = useState<CacheInfo | null>(null);
  const [rebuilding, setRebuilding] = useState(false);

  // ─── Import/Export state ──────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Load operations and distinct values on mount
  useEffect(() => {
    loadMetadata();
  }, []);

  const loadMetadata = useCallback(async () => {
    try {
      const [ops, mappings] = await Promise.all([fetchOperations(), fetchMappings()]);
      setOperations(ops);

      // Extract distinct permissions from current mappings (for grant autocomplete)
      const perms = [...new Set(mappings.map(m => m.permission))].sort();
      setDistinctPermissions(perms);

      // Load cache info
      const info = await fetchCacheInfo();
      setCacheInfo(info);
    } catch (err) {
      console.error('Failed to load permission metadata:', err);
    }
  }, []);

  // ─── Available operations for selected grant feature ───────────────────
  const operationsForFeature = useMemo(() => {
    if (!grantFeature) return [];
    return operations
      .filter(op => op.feature === grantFeature && op.isActive)
      .map(op => op.operation)
      .sort();
  }, [grantFeature, operations]);

  // Available features from operations table (distinct)
  const availableFeatures = useMemo(() => {
    return [...new Set(operations.map(op => op.feature))].sort();
  }, [operations]);

  // Reset operation when feature changes
  useEffect(() => {
    setGrantOperation('');
  }, [grantFeature]);

  // ─── Grant handler ─────────────────────────────────────────────────────
  const handleGrant = useCallback(async () => {
    if (!grantPermission || !grantFeature || !grantOperation) {
      setGrantError('All three fields are required');
      return;
    }

    setGranting(true);
    setGrantError('');
    setGrantSuccess('');

    try {
      await createMapping(grantPermission, grantFeature, grantOperation);
      setGrantSuccess(`Granted ${grantPermission} -> ${grantFeature}.${grantOperation}`);
      setGrantPermission('');
      setGrantFeature('');
      setGrantOperation('');
      setRefreshTrigger(prev => prev + 1);
      loadMetadata();
    } catch (err: any) {
      setGrantError(err.message || 'Failed to create mapping');
    } finally {
      setGranting(false);
    }
  }, [grantPermission, grantFeature, grantOperation, loadMetadata]);

  // ─── Delete handler ────────────────────────────────────────────────────
  const handleDelete = useCallback(async (entity: any) => {
    try {
      await deleteMapping(entity._id);
      setRefreshTrigger(prev => prev + 1);
      loadMetadata();
    } catch (err) {
      console.error('Failed to delete mapping:', err);
    }
  }, [loadMetadata]);

  // ─── Cache rebuild ─────────────────────────────────────────────────────
  const handleRebuildCache = useCallback(async () => {
    setRebuilding(true);
    try {
      const info = await rebuildCache();
      setCacheInfo(info);
    } catch (err) {
      console.error('Failed to rebuild cache:', err);
    } finally {
      setRebuilding(false);
    }
  }, []);

  // ─── Export handler ──────────────────────────────────────────────────
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await exportMappings();
    } catch (err) {
      console.error('Failed to export permissions:', err);
    } finally {
      setExporting(false);
    }
  }, []);

  // ─── Import modal close ─────────────────────────────────────────────
  const handleImportClose = useCallback((imported: boolean) => {
    setShowImportModal(false);
    if (imported) {
      setRefreshTrigger(prev => prev + 1);
      loadMetadata();
    }
  }, [loadMetadata]);

  // ─── Clear status messages after timeout ───────────────────────────────
  useEffect(() => {
    if (grantSuccess) {
      const timer = setTimeout(() => setGrantSuccess(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [grantSuccess]);

  useEffect(() => {
    if (grantError) {
      const timer = setTimeout(() => setGrantError(''), 6000);
      return () => clearTimeout(timer);
    }
  }, [grantError]);

  // ─── Toolbar (Grant + Cache + Import/Export — no filter controls) ─────
  const toolbar = (
    <div className="perm-toolbar">
      <div className="perm-toolbar-columns">
        {/* Grant section */}
        <div className="perm-toolbar-section">
          <span className="perm-toolbar-label">Grant</span>
          <div className="perm-toolbar-row">
            <label className="perm-filter-item">
              <span>Permission</span>
              <input
                type="text"
                placeholder="e.g. dispatcher"
                value={grantPermission}
                onChange={e => setGrantPermission(e.target.value.trim())}
                list="perm-suggestions"
              />
              <datalist id="perm-suggestions">
                {distinctPermissions.map(p => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
            <label className="perm-filter-item">
              <span>Feature</span>
              <select
                value={grantFeature}
                onChange={e => setGrantFeature(e.target.value)}
              >
                <option value="">Select...</option>
                {availableFeatures.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
            <label className="perm-filter-item">
              <span>Operation</span>
              <select
                value={grantOperation}
                onChange={e => setGrantOperation(e.target.value)}
                disabled={!grantFeature}
              >
                <option value="">Select...</option>
                {operationsForFeature.map(op => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>
            </label>
            <button
              className="btn btn-success btn-sm"
              onClick={handleGrant}
              disabled={granting || !grantPermission || !grantFeature || !grantOperation}
            >
              {granting ? 'Granting...' : 'Grant'}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setGrantPermission('');
                setGrantFeature('');
                setGrantOperation('');
                setGrantError('');
                setGrantSuccess('');
              }}
              disabled={granting}
              title="Clear form"
            >
              Clear
            </button>
          </div>
          {grantError && <div className="perm-status perm-status-error">{grantError}</div>}
          {grantSuccess && <div className="perm-status perm-status-success">{grantSuccess}</div>}
        </div>
      </div>

      {/* Help icon */}
      <div className="perm-help-row">
        <ContextHelp title='Help with Permissions' context='permission-manager-help' />
      </div>

      {/* Cache info strip + admin actions */}
      <div className="perm-cache-row">
        <span className="perm-cache-info">
          {cacheInfo
            ? `Cache: ${cacheInfo.totalMappings} mappings, ${cacheInfo.permissionCount} permissions`
            : 'Loading cache info...'}
        </span>
        <div className="perm-cache-actions">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleExport}
            disabled={exporting}
            title="Export all mappings as JSON"
          >
            <FontAwesomeIcon icon={faDownload} /> {exporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setShowImportModal(true)}
            title="Import mappings from JSON"
          >
            <FontAwesomeIcon icon={faUpload} /> Import
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleRebuildCache}
            disabled={rebuilding}
          >
            {rebuilding ? 'Rebuilding...' : 'Rebuild Cache'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`permission-manager-explorer ${className || ''}`}>
      <ConfigurableBaseExplorer<any>
        config={permissionManagerConfig}
        isAuthenticated={isLoggedIn}
        isWindowed={true}
        windowTitle="Permission Manager"
        initialPosition={initialPosition}
        initialSize={initialSize}
        resizable={true}
        draggable={true}
        showCloseButton={true}
        showMinimizeButton={true}
        onWindowClose={onClose}
        className="permission-manager-configured"
        customToolbar={toolbar}
        refreshTrigger={refreshTrigger}
        onEntityDelete={handleDelete}
      />
      {showImportModal && createPortal(
        <ImportPermissionsModal
          operations={operations}
          onClose={handleImportClose}
        />,
        document.body,
      )}
    </div>
  );
};

export default PermissionManagerExplorer;
